/* eslint-disable @typescript-eslint/naming-convention */

import TrustpilotLogo from 'components/SVG/TrustpilotLogo'
import React from 'react'
import { AmplitudeEventTypes, sendAmplitudeEvent } from 'services/amplitude'
import styled from 'styled-components'
import {
  ElementFactory,
  Question,
  ReactQuestionFactory,
  Serializer,
  SurveyQuestionElementBase,
} from 'survey-react'

const questionType = 'revamped-welcome'

export class RevampedWelcomeQuestion extends Question {
  getType() {
    return questionType
  }
}

export function registerWelcomeQuestion() {
  ElementFactory.Instance.registerElement(questionType, name => new RevampedWelcomeQuestion(name))
  ReactQuestionFactory.Instance.registerQuestion(questionType, props => {
    return React.createElement(RevampedWelcome, props)
  })
  Serializer.addClass(questionType, [], () => new RevampedWelcomeQuestion(''))
  return questionType
}

const maxWidth = 1000

const Page = styled.div(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  margin: '0 auto',
  maxWidth,
  minHeight: '100vh',
  width: '100%',

  [theme.breakpoints.up(theme.breakpoints.md)]: {
    gap: 24,
    justifyContent: 'center',
  },
}))

const TopBar = styled.header(({ theme }) => ({
  alignItems: 'center',
  backgroundColor: theme.colors.popGreen200,
  boxSizing: 'border-box',
  color: theme.colors.popGreen500,
  display: 'flex',
  fontSize: 14,
  fontWeight: 500,
  gap: 8,
  justifyContent: 'center',
  letterSpacing: 0.14,
  lineHeight: '20px',
  padding: '12px 16px',
  width: '100%',

  '& > *': {
    textAlign: 'initial',
  },

  [theme.breakpoints.up(theme.breakpoints.md)]: {
    height: 55,
  },
}))

const RatingContainer = styled.span({
  alignItems: 'flex-start',
  display: 'flex',
  gap: 4,
})

const ContentContainer = styled.section(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  margin: '0 auto',
  maxWidth: 480,
  padding: '0 16px',

  [theme.breakpoints.up(theme.breakpoints.md)]: {
    alignItems: 'center',
    display: 'grid',
    gap: 24,
    gridTemplateColumns: 'repeat(2, 1fr)',
    maxWidth: '100%',
  },

  [theme.breakpoints.up(`${maxWidth + 16}px`)]: {
    padding: 0,
  },
}))

const ImageContainer = styled.div(({ theme }) => ({
  alignItems: 'center',
  display: 'flex',
  height: 182,
  justifyContent: 'center',
  overflow: 'hidden',
  position: 'relative',

  [theme.breakpoints.up(theme.breakpoints.md)]: {
    alignSelf: 'flex-end',
    height: 410,
  },
}))

const Image = styled.img(({ theme }) => ({
  height: 400,
  objectFit: 'cover',
  objectPosition: 'center',
  position: 'absolute',
  bottom: '-124px',

  [theme.breakpoints.up(theme.breakpoints.md)]: {
    height: 'auto',
    position: 'relative',
    top: 40,
    width: 1015.29376,
  },
}))

const H1 = styled.h1(({ theme }) => ({
  color: theme.colors.primary400,
  fontFamily: theme.fonts.quincy,
  fontSize: 38,
  fontWeight: 300,
  lineHeight: '46px',
  margin: '0 0 12px',
  textAlign: 'initial',

  [theme.breakpoints.up(theme.breakpoints.md)]: {
    fontSize: 56,
    fontWeight: 300,
    lineHeight: '60px',
    margin: '0 0 16px',
  },
}))

const Ul = styled.ul({
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  listStyle: 'none',
  margin: 0,
  padding: 0,
})

const Li = styled.li(({ theme }) => ({
  color: theme.colors.obsidianBlack,
  display: 'flex',
  fontSize: 14,
  fontWeight: 500,
  gap: 8,
  letterSpacing: 0.14,
  lineHeight: '20px',
}))

const Info = styled.p(({ theme }) => ({
  backgroundColor: theme.colors.neutral100,
  borderRadius: '10px',
  color: theme.colors.neutral400,
  fontSize: 14,
  fontWeight: 500,
  letterSpacing: 0.14,
  lineHeight: '20px',
  margin: '24px 0',
  padding: 16,
  textAlign: 'initial',
}))

const Button = styled.button(({ theme }) => ({
  backgroundColor: theme.colors.obsidianBlack,
  border: 'none',
  borderRadius: 80,
  color: theme.colors.coolGrayish,
  cursor: 'pointer',
  fontFamily: theme.fonts.greycliff,
  fontSize: 18,
  fontWeight: 600,
  letterSpacing: 0.18,
  lineHeight: '24px',
  marginBottom: 24,
  paddingBottom: 13,
  paddingTop: 11,
  width: '100%',
}))

const Legal = styled.p(({ theme }) => ({
  color: `${theme.colors.neutral300} !important`,
  fontSize: 12,
  letterSpacing: 0.24,
  lineHeight: '120%',
  margin: 0,
  textAlign: 'initial',

  'a, a:visited': {
    color: 'inherit',
  },
}))

const CheckMark = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
    <path
      d="M17.3776 4.94785L9.15297 14.7492C8.79797 15.1722 8.16724 15.2274 7.74417 14.8725L3.2262 11.0817"
      stroke="#147D57"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
)

const path = '/prepurchase-survey/revamped-welcome'
const cta = 'Take 3 min eligibility quiz'

export class RevampedWelcome extends SurveyQuestionElementBase {
  constructor(props: unknown) {
    super(props)
  }

  componentDidMount() {
    this.props.creator.survey.setVariable('showPricing', false)
  }

  render(): JSX.Element {
    return (
      <Page>
        <TopBar>
          <span>Trusted by 250,000 people in the US</span>
          <RatingContainer>
            <b>4.2</b>
            <TrustpilotLogo width={78} />
          </RatingContainer>
        </TopBar>
        <ContentContainer>
          <ImageContainer>
            <Image
              alt="Possible Medications"
              src="https://img.joinfound.com/bothan/Welcome_vWN2BGRfX.png?updatedAt=1741205447148"
            />
          </ImageContainer>

          <div>
            <H1>Let’s find the right plan for you</H1>

            <Ul>
              <Li>
                <CheckMark /> Access to weight loss medication.
              </Li>
              <Li>
                <CheckMark />
                Covered by major insurance plans.
              </Li>
              <Li>
                <CheckMark />
                Support on your path to weight loss.
              </Li>
            </Ul>

            <Info>
              First, we’ll check your eligibility to provide a personalized price tailored to your
              needs and insurance coverage.
            </Info>

            <Button
              onClick={() => {
                sendAmplitudeEvent(AmplitudeEventTypes.ButtonClick, {
                  path,
                  text: cta,
                })
                this.props.creator.survey.nextPage()
              }}
            >
              {cta}
            </Button>

            <Legal>
              By providing your information in this quiz, you are agreeing to our{' '}
              <a
                href="https://joinfound.com/terms/privacy-policy"
                rel="noopener noreferrer"
                target="_blank"
              >
                Privacy Policy
              </a>{' '}
              and our{' '}
              <a
                href="https://joinfound.com/terms/privacy-practices-notice"
                rel="noopener noreferrer"
                target="_blank"
              >
                Notice of Privacy Practices
              </a>
              .
            </Legal>
          </div>
        </ContentContainer>
      </Page>
    )
  }
}
